import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import {
  assignSubCategoryToCategory,
  createSubCategoryQuick,
  fetchActiveSubCategories,
} from "../../../services/subcategories";
import {
  hiddenChoiceItemsFromKeep,
  syncProductChoicesAndItems,
} from "../../../services/MenuService";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import {
  mediaFileUrl,
  productChoiceChildren,
  productTitle,
  subcategoryTitle,
} from "../Cms/MenuTree/menuTreeUtils";
import ProductChoiceKeepPanel from "../Cms/MenuTree/ProductChoiceKeepPanel";
import "../../../i18n";
import "../Cms/MenuTree/MenuTree.css";
import "./AddCategory.css";

const DEFAULT_LANG = "en";

/**
 * Add category: photo (optional) · name in any language (at least one)
 * · subcategories to attach (optional).
 * On submit: create the category, then assign the chosen subcategories.
 */
const AddCategory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const languages = useMemo(() => getMenuLanguages(t), [t]);

  // ------------------------------------------------------------------ names
  const [titles, setTitles] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, ""]))
  );
  const [openLangs, setOpenLangs] = useState({});
  const setTitle = (code, value) => setTitles((prev) => ({ ...prev, [code]: value }));
  const filledTitle = (code) => titles[code]?.trim() || "";
  const hasAnyTitle = MENU_LANGUAGE_CODES.some((code) => filledTitle(code));
  const previewLang = MENU_LANGUAGE_CODES.find((code) => filledTitle(code)) || DEFAULT_LANG;
  const isOpen = (code) => Boolean(openLangs[code]) || Boolean(titles[code]?.trim());

  // ------------------------------------------------------------------ photo
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const fileInputRef = useRef(null);
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setImageUrl(URL.createObjectURL(selected));
    event.target.value = "";
  };

  // ---------------------------------------------------------- subcategories
  const [allSubs, setAllSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);
  const [selectedSubIds, setSelectedSubIds] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [newSubName, setNewSubName] = useState("");
  const [creatingSub, setCreatingSub] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  /** { [subId]: productId[] } — missing = every product kept (same as Manage menu). */
  const [kept, setKept] = useState({});
  const [openPanels, setOpenPanels] = useState({});
  const [subsOpen, setSubsOpen] = useState(false);
  const [keptChoices, setKeptChoices] = useState({});
  const [keptItems, setKeptItems] = useState({});
  const [openProductChoices, setOpenProductChoices] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchActiveSubCategories();
        if (!cancelled) setAllSubs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("AddCategory: subcategories load failed", err);
      } finally {
        if (!cancelled) setSubsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const subsById = useMemo(
    () => Object.fromEntries(allSubs.map((s) => [String(s._id), s])),
    [allSubs]
  );
  const query = subSearch.trim().toLowerCase();
  const visibleSubs = allSubs.filter(
    (s) => !query || subcategoryTitle(s, "").toLowerCase().includes(query)
  );

  const productsOf = (sub) =>
    (sub?.productList || [])
      .filter((p) => p?.product != null && p.product.isArchived !== true)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((p) => ({
        id: String(p.product._id || p.product),
        label: productTitle(p.product),
        product: p.product,
      }))
      .filter((p) => p.id && p.id !== "undefined");

  const keptIdsFor = (subId, products) => {
    const all = products.map((p) => p.id);
    const custom = kept[subId];
    return Array.isArray(custom) ? all.filter((id) => custom.includes(id)) : all;
  };

  const PREVIEW_CAT_ID = "preview-category";
  const livePreview = useMemo(() => {
    const categoryDetails = MENU_LANGUAGE_CODES.map((code) => ({
      language: code,
      categoryTitle: titles[code]?.trim() || "",
      categoryDescription: "",
    })).filter((detail) => detail.categoryTitle);

    const products = [];
    const productPhotos = {};
    const seenProducts = new Set();
    const subcategories = [];
    const subcategoryProducts = {};
    const categorySubcategories = [];

    selectedSubIds.forEach((subId) => {
      const sub = subsById[subId];
      if (!sub) return;
      categorySubcategories.push(subId);
      subcategories.push({
        tempId: subId,
        primaryLanguage: sub.primaryLanguage || DEFAULT_LANG,
        subCategoryDetails: sub.subCategoryDetails,
        displayOrientation:
          sub.displayOrientation === "horizontal" ? "horizontal" : "vertical",
      });
      const rows = productsOf(sub);
      const keptIds = keptIdsFor(subId, rows);
      subcategoryProducts[subId] = keptIds;
      rows.forEach((row) => {
        if (!keptIds.includes(row.id) || seenProducts.has(row.id)) return;
        seenProducts.add(row.id);
        const product = row.product && typeof row.product === "object" ? row.product : null;
        products.push({
          tempId: row.id,
          price: product?.price ?? 0,
          secondaryPrice: product?.secondaryPrice,
          productDetails: product?.productDetails,
          primaryLanguage: product?.primaryLanguage,
        });
        if (!product || product.imageDisabled) return;
        const url = mediaFileUrl(
          "product",
          Array.isArray(product.photos) ? product.photos[0] : product.photos
        );
        if (url) productPhotos[row.id] = url;
      });
    });

    return {
      draft: {
        visualSettingsMode: "custom",
        menu: {
          primaryLanguage: previewLang,
          menuDetails: [{ language: previewLang, menuName: "" }],
          productImagesDisabled: false,
          productPricesHidden: false,
          searchBarHidden: false,
        },
        restaurant: {
          clientMenuShowLogo: true,
          clientShowAllMenusOnOnePage: false,
        },
        products,
        categories: [
          {
            tempId: PREVIEW_CAT_ID,
            primaryLanguage: previewLang,
            categoryDetails,
          },
        ],
        subcategories,
        relations: {
          menuCategories: [PREVIEW_CAT_ID],
          categoryProducts: { [PREVIEW_CAT_ID]: [] },
          categorySubcategories: { [PREVIEW_CAT_ID]: categorySubcategories },
          subcategoryProducts,
        },
      },
      productPhotos,
    };
  }, [titles, previewLang, selectedSubIds, kept, subsById]);

  const toggleSub = (id) => {
    const wasSelected = selectedSubIds.includes(id);
    setSelectedSubIds((prev) => (wasSelected ? prev.filter((x) => x !== id) : [...prev, id]));
    if (!wasSelected) setOpenPanels((prev) => ({ ...prev, [id]: true }));
  };

  const setKeptFor = (subId, ids) => setKept((prev) => ({ ...prev, [subId]: ids }));

  const togglePanel = (id, fallback) =>
    setOpenPanels((prev) => ({ ...prev, [id]: !(prev[id] ?? fallback) }));

  const cancelCreateSub = () => {
    if (creatingSub) return;
    setCreatingNew(false);
    setNewSubName("");
  };

  const handleCreateSub = async () => {
    const name = newSubName.trim();
    if (!name || creatingSub) return;
    setCreatingSub(true);
    try {
      const created = await createSubCategoryQuick(name);
      if (!created?._id) throw new Error("no id");
      const id = String(created._id);
      setAllSubs((prev) => (prev.some((s) => String(s._id) === id) ? prev : [created, ...prev]));
      setSelectedSubIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setOpenPanels((prev) => ({ ...prev, [id]: true }));
      setNewSubName("");
      setSubSearch("");
      setCreatingNew(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || t("catFormSubcategoryCreateFailed"));
    } finally {
      setCreatingSub(false);
    }
  };

  // ----------------------------------------------------------------- submit
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = hasAnyTitle && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    const categoryDetails = languages
      .map((l) => ({
        language: l.code,
        categoryTitle: titles[l.code]?.trim() || "",
        categoryDescription: "",
      }))
      .filter((d) => d.categoryTitle);

    const formData = new FormData();
    formData.append("data", JSON.stringify({ categoryDetails, displayOrientation: "vertical" }));
    if (file) formData.append("file", file);

    setSubmitting(true);
    let created;
    try {
      const response = await axios.post(`${config.dev}/v2/categories/create`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      });
      created = response.data;
    } catch (err) {
      console.error("AddCategory: create failed", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || t("catFormCreateFailed"));
      setSubmitting(false);
      return;
    }

    if (selectedSubIds.length && created?._id) {
      try {
        await assignSubCategoryToCategory({
          categoryId: String(created._id),
          subcategories: selectedSubIds.map((subCategoryId, index) => ({
            subCategoryId,
            order: index + 1,
          })),
        });
        const synced = new Set();
        for (const subId of selectedSubIds) {
          const products = productsOf(subsById[subId]);
          const keptProductIds = keptIdsFor(subId, products);
          for (const product of products) {
            if (!keptProductIds.includes(product.id) || synced.has(product.id)) continue;
            const children = productChoiceChildren(product.product);
            if (!children.length) continue;
            synced.add(product.id);
            const keptChoiceIds = keptChoices[product.id] ?? children.map((c) => c.id);
            await syncProductChoicesAndItems(
              product.id,
              keptChoiceIds,
              hiddenChoiceItemsFromKeep(children, keptChoiceIds, keptItems[product.id] || {})
            );
          }
        }
      } catch (err) {
        console.error("AddCategory: assign subcategories failed", err);
        toast.warn(t("catFormAttachFailed"));
      }
    }

    swal({ title: t("successTitle"), text: t("categoryCreated"), icon: "success", timer: 1000, buttons: false });
    setTimeout(() => navigate("/categories"), 1000);
  };

  // ----------------------------------------------------------------- render
  const langInput = (lang, required = false) => (
    <div className="catform__field" key={lang.code}>
      <label className="catform__label" htmlFor={`cat-title-${lang.code}`}>
        <img src={lang.flag} alt="" />
        {lang.label}
        {required && <span className="catform__required">{t("catFormRequired")}</span>}
      </label>
      <input
        id={`cat-title-${lang.code}`}
        type="text"
        className="catform__input"
        value={titles[lang.code]}
        autoFocus
        onChange={(e) => setTitle(lang.code, e.target.value)}
      />
    </div>
  );

  return (
    <>
      <PageTitle activeMenu={t("addCategory")} motherMenu={t("kitchen")} />

      <div className="catform-with-preview">
      <form className="catform" onSubmit={handleSubmit} noValidate>
        <div className="catform__top">
          <h1 className="catform__title">{t("catFormTitle")}</h1>
          <button type="button" className="btn catform__link" onClick={() => navigate("/categories")}>
            <i className="fas fa-arrow-left" aria-hidden /> {t("categoriesList")}
          </button>
        </div>

        {/* ---------------------------------------------------------- photo */}
        <section className="catform__section">
          <h2 className="catform__heading">
            {t("catFormPhoto")} <span className="catform__optional">{t("optional")}</span>
          </h2>
          <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={handleFileChange} />
          <div className="catform__photo">
            <button type="button" className="catform__thumb" onClick={() => fileInputRef.current?.click()}>
              {imageUrl ? <img src={imageUrl} alt="" /> : <i className="fas fa-camera" aria-hidden />}
            </button>
            <div className="catform__photo-actions">
              <button type="button" className="btn catform__small" onClick={() => fileInputRef.current?.click()}>
                {imageUrl ? t("catFormReplacePhoto") : t("catFormChoosePhoto")}
              </button>
              {imageUrl && (
                <button
                  type="button"
                  className="btn catform__small is-danger"
                  onClick={() => {
                    setFile(null);
                    setImageUrl(null);
                  }}
                >
                  {t("catFormRemovePhoto")}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------------- name */}
        <section className="catform__section">
          <h2 className="catform__heading">{t("catFormStepName")}</h2>
          <p className="catform__pills-label">{t("catFormChooseLanguage")}</p>
          <div className="catform__pills">
            {languages.map((lang) => {
              const filled = Boolean(titles[lang.code]?.trim());
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`catform__pill ${isOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                  aria-pressed={isOpen(lang.code)}
                  onClick={() => setOpenLangs((prev) => ({ ...prev, [lang.code]: !prev[lang.code] }))}
                >
                  <img src={lang.flag} alt="" />
                  {lang.label}
                  {filled && <i className="fas fa-check" aria-hidden />}
                </button>
              );
            })}
          </div>
          <div className="catform__lang-fields">
            {languages.filter((lang) => isOpen(lang.code)).map((lang) => langInput(lang))}
          </div>
        </section>

        {/* -------------------------------------------------- subcategories */}
        <section className={`catform__section ${subsOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="catform__fold"
            aria-expanded={subsOpen}
            aria-controls="catform-subs"
            onClick={() => setSubsOpen((open) => !open)}
          >
            <h2 className="catform__heading">
              {t("catFormStepSubcategories")} <span className="catform__optional">{t("optional")}</span>
              {selectedSubIds.length > 0 && (
                <span className="catform__fold-count">{selectedSubIds.length}</span>
              )}
            </h2>
            <i
              className={`fas fa-chevron-down catform__fold-arrow ${subsOpen ? "is-open" : ""}`}
              aria-hidden
            />
          </button>

          <p className="catform__help">{t("catFormSubcategoriesHelp")}</p>

          {subsOpen && (
          <div id="catform-subs" className="catform__fold-body">

          {selectedSubIds.length > 0 && (
            <div className="catform__chips">
              {selectedSubIds.map((id) => (
                <span key={id} className="catform__chip">
                  {subcategoryTitle(subsById[id], t("catFormUnknownSubcategory"))}
                  <button type="button" aria-label={t("remove")} onClick={() => toggleSub(id)}>
                    <i className="fas fa-times" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <input
            type="search"
            className="catform__input"
            placeholder={t("catFormSearchSubcategories")}
            value={subSearch}
            onChange={(e) => setSubSearch(e.target.value)}
          />

          <div className="catform__list">
            {subsLoading ? (
              <div className="catform__empty">{t("loading")}…</div>
            ) : allSubs.length === 0 ? (
              <div className="catform__empty">{t("catFormNoSubcategories")}</div>
            ) : visibleSubs.length === 0 ? (
              <div className="catform__empty">{t("catFormNoMatch")}</div>
            ) : (
              visibleSubs.map((sub) => {
                const id = String(sub._id);
                const checked = selectedSubIds.includes(id);
                const products = productsOf(sub);
                const total = products.length;
                const keptIds = keptIdsFor(id, products);
                const keptCount = keptIds.length;
                const hasPanel = checked && total > 0;
                const panelOpen = hasPanel && (openPanels[id] ?? true);
                const panelId = `catform-panel-${id}`;
                return (
                  <div key={id} className={`catform__item ${checked ? "is-checked" : ""} ${panelOpen ? "is-expanded" : ""}`}>
                    <div className="catform__item-head">
                      <label className={`catform__option ${checked ? "is-checked" : ""}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleSub(id)} />
                        <span className="catform__option-name">{subcategoryTitle(sub)}</span>
                        {total > 0 && (
                          <span className={`menu-picker__badge ${hasPanel ? "menu-picker__badge--kept" : ""}`}>
                            {hasPanel
                              ? t("menuTreeKeptBadge", { kept: keptCount, total })
                              : t("menuTreeProductsBadge", { count: total })}
                          </span>
                        )}
                      </label>
                      {hasPanel && (
                        <button
                          type="button"
                          className={`btn menu-picker__chevron ${panelOpen ? "is-open" : ""}`}
                          onClick={() => togglePanel(id, true)}
                          aria-expanded={panelOpen}
                          aria-controls={panelId}
                          aria-label={panelOpen ? t("menuTreeCollapse") : t("menuTreeExpand")}
                        >
                          <i className="fas fa-chevron-down" aria-hidden />
                        </button>
                      )}
                    </div>

                    {panelOpen && (
                      <div className="menu-picker__panel" id={panelId}>
                        <div className="menu-picker__panel-head">
                          <span className="menu-picker__panel-summary">
                            <strong>{keptCount}</strong> {t("menuTreeKeptSummary", { total })}
                          </span>
                          <button
                            type="button"
                            className="btn menu-picker__panel-toggle"
                            onClick={() => setKeptFor(id, keptCount === total ? [] : products.map((p) => p.id))}
                          >
                            {keptCount === total ? t("menuTreeUncheckAll") : t("menuTreeCheckAll")}
                          </button>
                        </div>
                        <div
                          className="menu-picker__progress"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={total}
                          aria-valuenow={keptCount}
                        >
                          <span style={{ width: `${total ? (keptCount / total) * 100 : 0}%` }} />
                        </div>
                        <div className="menu-picker__products">
                          {products.map((product) => {
                            const on = keptIds.includes(product.id);
                            const switchId = `catform-${id}-${product.id}`;
                            const initial = String(product.label || "?").trim().charAt(0).toUpperCase();
                            const choices = productChoiceChildren(product.product);
                            const choiceKey = `${id}:${product.id}`;
                            const choicePanelOpen =
                              on &&
                              choices.length > 0 &&
                              (openProductChoices[choiceKey] ?? true);
                            return (
                              <div key={product.id}>
                                <div className="menu-picker__head">
                                  <label
                                    htmlFor={switchId}
                                    className={`menu-picker__product ${on ? "is-on" : ""}`}
                                  >
                                    <span className="menu-picker__avatar" aria-hidden>
                                      {initial || "?"}
                                    </span>
                                    <span className="menu-picker__product-label">{product.label}</span>
                                    {choices.length > 0 && (
                                      <span
                                        className={`menu-picker__badge ${
                                          on ? "menu-picker__badge--kept" : ""
                                        }`}
                                      >
                                        {on
                                          ? t("menuTreeKeptBadge", {
                                              kept: (keptChoices[product.id] ?? choices.map((c) => c.id))
                                                .length,
                                              total: choices.length,
                                            })
                                          : t("menuTreeChoicesBadge", { count: choices.length })}
                                      </span>
                                    )}
                                    <input
                                      id={switchId}
                                      type="checkbox"
                                      className="menu-picker__switch"
                                      role="switch"
                                      checked={on}
                                      aria-checked={on}
                                      onChange={() =>
                                        setKeptFor(
                                          id,
                                          on
                                            ? keptIds.filter((pid) => pid !== product.id)
                                            : [...keptIds, product.id]
                                        )
                                      }
                                    />
                                  </label>
                                  {on && choices.length > 0 && (
                                    <button
                                      type="button"
                                      className={`btn menu-picker__chevron ${choicePanelOpen ? "is-open" : ""}`}
                                      onClick={() =>
                                        setOpenProductChoices((prev) => ({
                                          ...prev,
                                          [choiceKey]: !(prev[choiceKey] ?? true),
                                        }))
                                      }
                                      aria-expanded={choicePanelOpen}
                                    >
                                      <i className="fas fa-chevron-down" aria-hidden />
                                    </button>
                                  )}
                                </div>
                                {choicePanelOpen && (
                                  <div className="menu-picker__child-panel">
                                    <ProductChoiceKeepPanel
                                      productId={product.id}
                                      choices={choices}
                                      keptChoiceIds={keptChoices[product.id]}
                                      onKeptChoicesChange={(ids) =>
                                        setKeptChoices((prev) => ({ ...prev, [product.id]: ids }))
                                      }
                                      keptItems={keptItems[product.id] || {}}
                                      onKeptItemsChange={(choiceId, ids) =>
                                        setKeptItems((prev) => ({
                                          ...prev,
                                          [product.id]: { ...(prev[product.id] || {}), [choiceId]: ids },
                                        }))
                                      }
                                      t={t}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="catform__new">
            {creatingNew ? (
              <div className="menu-picker__create-form">
                <input
                  type="text"
                  className="menu-picker__create-input"
                  placeholder={t("catFormNewSubcategoryPlaceholder")}
                  aria-label={t("catFormNewSubcategoryPlaceholder")}
                  value={newSubName}
                  disabled={creatingSub}
                  autoFocus
                  onChange={(e) => setNewSubName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateSub();
                    }
                    if (e.key === "Escape") cancelCreateSub();
                  }}
                />
                <button
                  type="button"
                  className="btn menu-picker__create-submit"
                  disabled={!newSubName.trim() || creatingSub}
                  onClick={handleCreateSub}
                >
                  {creatingSub ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  ) : (
                    t("menuTreeCreate")
                  )}
                </button>
                <button
                  type="button"
                  className="btn menu-picker__create-cancel"
                  onClick={cancelCreateSub}
                  disabled={creatingSub}
                  aria-label={t("cancel")}
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
            ) : (
              <button type="button" className="btn menu-picker__create" onClick={() => setCreatingNew(true)}>
                <span className="menu-picker__create-plus" aria-hidden>
                  <i className="fas fa-plus" />
                </span>
                {t("menuTreeNewSubcategory")}
              </button>
            )}
          </div>
          </div>
          )}
        </section>

        {/* -------------------------------------------------------- actions */}
        <div className="catform__actions">
          <button type="button" className="btn catform__btn catform__btn--cancel" onClick={() => navigate("/categories")} disabled={submitting}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn catform__btn catform__btn--primary" disabled={!canSubmit}>
            {submitting ? t("catFormCreating") : t("catFormCreate")}
          </button>
        </div>
      </form>

      <aside className="menu-manage-live" aria-label={t("wizardPreviewLive")}>
        <WizardLivePhone
          draft={livePreview.draft}
          productPhotos={livePreview.productPhotos}
          compact
          showStepLabel={false}
          emptyHintKey="catFormLiveEmpty"
        />
      </aside>
      </div>
    </>
  );
};

export default AddCategory;
